import { ContentHeader } from "../components/ContentHeader";
import { useApi } from "../hooks/useApi";

type OblStatus = "expired" | "critical" | "warning" | "ok" | "unrecorded";

interface Cert {
  id: string; name: string; type: string; owner?: string;
  expires_on: string | null; days_until: number | null; status: OblStatus;
  renewal_url?: string | null; source?: string; probe_error?: boolean; notes?: string;
}
interface IntKey {
  id: string; partner: string; key_type: string; owner?: string;
  expires_on: string | null; days_until: number | null; status: OblStatus; rotation_notes?: string;
}
interface Vendor {
  id: string; name: string; category?: string; owner?: string;
  monthly_budget_sar: number | null; renewal_on: string | null;
  days_until: number | null; status: OblStatus; console_url?: string | null; notes?: string;
}
interface ObligationsResponse {
  status: "ok" | "unavailable";
  reason?: string;
  certificates: Cert[];
  integration_keys: IntKey[];
  vendors: Vendor[];
  summary: { expired?: number; critical?: number; warning?: number; unrecorded?: number; total?: number };
}

const RANK: Record<OblStatus, number> = { expired: 0, critical: 1, warning: 2, unrecorded: 3, ok: 4 };
const META: Record<OblStatus, { label: string; color: string }> = {
  expired:    { label: "Expired",     color: "var(--red)" },
  critical:   { label: "<7 days",     color: "var(--red)" },
  warning:    { label: "<30 days",    color: "var(--yellow)" },
  unrecorded: { label: "No date",     color: "var(--text-faint)" },
  ok:         { label: "OK",          color: "var(--green)" },
};

function sortByUrgency<T extends { status: OblStatus; days_until: number | null }>(rows: T[]): T[] {
  return [...rows].sort((a, b) =>
    RANK[a.status] - RANK[b.status] || (a.days_until ?? 99999) - (b.days_until ?? 99999));
}

function StatusBadge({ s }: { s: OblStatus }) {
  const m = META[s];
  return <span className="grade-badge" style={{ color: m.color, borderColor: m.color, fontSize: 11 }}>{m.label}</span>;
}

function whenLabel(dateStr: string | null, days: number | null): string {
  if (!dateStr) return "— not recorded";
  if (days == null) return dateStr;
  if (days < 0) return `${dateStr} · ${Math.abs(days)}d ago`;
  return `${dateStr} · in ${days}d`;
}

export function RenewalsTab() {
  const { data, loading, error } = useApi<ObligationsResponse>("/command-center/obligations");
  const title = "Renewals & Obligations";
  const desc = "Certificates, partner keys, and vendor contracts — what's expiring and who owns the renewal.";

  if (loading) {
    return (<><ContentHeader title={title} description={desc} />
      <div className="loading"><div className="loading-spinner" /><p>Checking certificates & renewals…</p></div></>);
  }
  if (error || !data) {
    return (<><ContentHeader title={title} description={desc} />
      <div className="empty">Could not load obligations. Is command-center-api running?</div></>);
  }
  if (data.status === "unavailable") {
    return (<><ContentHeader title={title} description={desc} />
      <div className="empty">{data.reason ?? "obligations.json not available."}</div></>);
  }

  const s = data.summary;
  const certs = sortByUrgency(data.certificates);
  const keys = sortByUrgency(data.integration_keys);
  const vendors = sortByUrgency(data.vendors);

  return (
    <>
      <ContentHeader title={title} count={s.total} description={desc} />

      <div className="cov-summary">
        <span className={`cov-pill ${s.expired ? "crit" : "ok"}`}><span className="dot" /><strong>{s.expired ?? 0}</strong> expired</span>
        <span className={`cov-pill ${s.critical ? "crit" : "ok"}`}><span className="dot" /><strong>{s.critical ?? 0}</strong> &lt;7 days</span>
        <span className={`cov-pill ${s.warning ? "warn" : "ok"}`}><span className="dot" /><strong>{s.warning ?? 0}</strong> &lt;30 days</span>
        <span className="cov-pill"><span className="dot" /><strong>{s.unrecorded ?? 0}</strong> no date</span>
      </div>

      <div className="section-header"><h2>Certificates</h2><span className="count">{certs.length}</span></div>
      <table className="cov-matrix">
        <thead><tr><th>Certificate</th><th>Type</th><th>Owner</th><th>Expires</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {certs.map((c) => (
            <tr key={c.id}>
              <td>{c.name}{c.source === "probe" && <span style={{ color: "var(--text-faint)", fontSize: 11 }}> · auto</span>}{c.probe_error && <span style={{ color: "var(--yellow)", fontSize: 11 }}> · probe failed</span>}</td>
              <td style={{ color: "var(--text-muted)" }}>{c.type}</td>
              <td style={{ color: "var(--text-muted)" }}>{c.owner ?? "—"}</td>
              <td style={{ fontVariantNumeric: "tabular-nums" }}>{whenLabel(c.expires_on, c.days_until)}</td>
              <td><StatusBadge s={c.status} /></td>
              <td>{c.renewal_url && <a href={c.renewal_url} target="_blank" rel="noopener">renew ↗</a>}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="section-header"><h2>Integration keys</h2><span className="count">{keys.length}</span></div>
      <table className="cov-matrix">
        <thead><tr><th>Partner</th><th>Key</th><th>Owner</th><th>Expires</th><th>Status</th></tr></thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id}>
              <td>{k.partner}</td>
              <td style={{ color: "var(--text-muted)" }}>{k.key_type}</td>
              <td style={{ color: "var(--text-muted)" }}>{k.owner ?? "—"}</td>
              <td style={{ fontVariantNumeric: "tabular-nums" }}>{whenLabel(k.expires_on, k.days_until)}</td>
              <td><StatusBadge s={k.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="section-header"><h2>Vendors — spend & renewals</h2><span className="count">{vendors.length}</span></div>
      <table className="cov-matrix">
        <thead><tr><th>Vendor</th><th>Category</th><th>Owner</th><th>Monthly budget</th><th>Renews</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {vendors.map((v) => (
            <tr key={v.id}>
              <td>{v.name}</td>
              <td style={{ color: "var(--text-muted)" }}>{v.category ?? "—"}</td>
              <td style={{ color: "var(--text-muted)" }}>{v.owner ?? "—"}</td>
              <td style={{ fontVariantNumeric: "tabular-nums" }}>{v.monthly_budget_sar != null ? `${v.monthly_budget_sar.toLocaleString()} SAR` : "— not set"}</td>
              <td style={{ fontVariantNumeric: "tabular-nums" }}>{whenLabel(v.renewal_on, v.days_until)}</td>
              <td><StatusBadge s={v.status} /></td>
              <td>{v.console_url && <a href={v.console_url} target="_blank" rel="noopener">console ↗</a>}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 18, fontSize: 11.5, color: "var(--text-muted)" }}>
        TLS certs are auto-probed live. Integration certs, partner keys, and vendor dates are declared in{" "}
        <code>obligations.json</code> — fill in the <strong>“no date”</strong> rows. Live vendor spend (Twilio/Unifonic/Otto usage APIs) is a follow-up.
      </div>
    </>
  );
}
